import {MRT_ColumnDef, MRT_Cell, MRT_Row} from 'material-react-table';
import React, {useMemo} from 'react';
import formattedDate from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import RecruitmentActionsMenu from '@/views/creators/recruit-crm/components/recruitment-actions-menu';
import StatusBadge from '@/views/creators/recruit-crm/components/status-badge';
import {
  PAGE_SELECTION_STATE,
  PageSelectionState,
} from '@/views/creators/recruit-crm/hooks/useRecruitCrmState';
import {Recruitment} from '@/views/creators/recruit-crm/types';
import Checkbox from '@/components/checkbox';
import styles from './useRecruitmentsTableColumns.module.scss';

export interface RecruitCrmTableActions {
  onRemove: (recruitmentId: string) => void;
  onRestoreToCrm: (recruitment: Recruitment) => void;
  onStartVetting: (recruitment: Recruitment) => void;
  isStartVettingLoading: boolean;
  onRowSelect: (id: string, selected: boolean) => void;
  rowSelection: Record<string, boolean>;
  pageSelectionState: PageSelectionState;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

/**
 * Hook to define Material React Table columns for Recruitments table
 *
 * @returns Array of column definitions for MRT
 */
function useRecruitmentsTableColumns(actions: RecruitCrmTableActions) {
  const {
    onRemove,
    onRestoreToCrm,
    onStartVetting,
    isStartVettingLoading,
    onRowSelect,
    pageSelectionState,
    onSelectAll,
    onClearSelection,
  } = actions;
  const columns = useMemo<MRT_ColumnDef<Recruitment>[]>(
    () => [
      {
        id: 'select',
        header: '',
        Header: () => (
          <div
            className={styles.selectCell}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Checkbox
              checked={pageSelectionState === PAGE_SELECTION_STATE.all}
              indeterminate={
                pageSelectionState === PAGE_SELECTION_STATE.partial
              }
              onChange={() => {
                if (pageSelectionState !== PAGE_SELECTION_STATE.all) {
                  onSelectAll();
                } else {
                  onClearSelection();
                }
              }}
            />
          </div>
        ),
        size: 70,
        grow: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({row}: {row: MRT_Row<Recruitment>}) => (
          <div
            className={styles.selectCell}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Checkbox
              checked={row.getIsSelected()}
              onChange={(checked: boolean) =>
                onRowSelect(row.original.id, checked)
              }
            />
            <RecruitmentActionsMenu
              recruitment={row.original}
              onRemove={onRemove}
              onRestoreToCrm={onRestoreToCrm}
              onStartVetting={onStartVetting}
              isStartVettingLoading={isStartVettingLoading}
            />
          </div>
        ),
      },
      {
        id: 'name',
        accessorFn: (row) => getRecruitmentFullName(row),
        header: translate('Name'),
        enableSorting: true,
        Cell: ({row}: {row: MRT_Cell<Recruitment>['row']}) => {
          return <div>{getRecruitmentFullName(row.original)}</div>;
        },
      },
      {
        accessorKey: 'email',
        header: translate('Email'),
        enableSorting: true,
        Cell: ({row}: {row: MRT_Cell<Recruitment>['row']}) => {
          const {email} = row.original;
          return <div style={{fontWeight: 500}}>{email || '-'}</div>;
        },
      },
      {
        accessorKey: 'status',
        header: translate('Status'),
        enableSorting: true,
        Cell: ({row}: {row: MRT_Cell<Recruitment>['row']}) => {
          const {status, removed} = row.original;
          return <StatusBadge status={status} removed={removed} />;
        },
      },
      {
        accessorKey: 'createdAt',
        header: translate('Recruited Date'),
        enableSorting: true,
        Cell: ({row}: {row: MRT_Cell<Recruitment>['row']}) => {
          const {createdAt} = row.original;
          const formattedCreatedAt = formattedDate(
            new Date(createdAt),
            MONTH_SHORT_DAY_YEAR_FORMAT
          );
          return <div>{formattedCreatedAt}</div>;
        },
      },
    ],
    [
      onRemove,
      onRestoreToCrm,
      onStartVetting,
      isStartVettingLoading,
      onRowSelect,
      pageSelectionState,
      onSelectAll,
      onClearSelection,
    ]
  );

  return columns;
}

function getRecruitmentFullName(recruitment: Recruitment): string {
  const firstName = recruitment.firstName?.trim();
  const lastName = recruitment.lastName?.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return fullName || '-';
}

export default useRecruitmentsTableColumns;
